import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Button from '../../components/Button/Button';
import { downloadFlightConfirmationPdf } from '../../lib/buildFlightConfirmationPdf';
import { summarizeConfirmedFlightOrder } from '../../lib/flightOrderDetails';
import { createFlightOrder } from '../../lib/duffelFlightsApi';
import './FlightBookingConfirmation.css';

const PAY_STORAGE_PREFIX = 'tourasco_pay_';

/** Suffixes — avoid clashing with payment payload key `tourasco_pay_${resumeKey}`. */
const orderDoneSuffix = '_flight_order_done';
const bookingLockSuffix = '_flight_booking_lock';

function isAlreadyBookedDuffelError(message) {
  const m = String(message || '').toLowerCase();
  return m.includes('already been booked') || m.includes('offer request that has already been booked');
}

const formatDt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const legDuration = (dep, arr) => {
  if (!dep || !arr) return '';
  const ms = new Date(arr) - new Date(dep);
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

const FlightBookingConfirmation = ({ onGoHome, onNavigate }) => {
  const { formatFrom } = useCurrency();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeKey = searchParams.get('payment_resume');
  const sessionId = searchParams.get('session_id');

  const [error, setError] = useState('');
  const [working, setWorking] = useState(Boolean(resumeKey && sessionId));
  const [order, setOrder] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pollIntervalRef = useRef(null);

  const detail = useMemo(() => summarizeConfirmedFlightOrder(order), [order]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!resumeKey && !sessionId) {
      setWorking(false);
      return undefined;
    }
    if (!resumeKey || !sessionId) {
      setError('Invalid payment link. Complete checkout from the payment page or start your booking again.');
      setWorking(false);
      return undefined;
    }

    const storageKey = PAY_STORAGE_PREFIX + resumeKey;
    const orderDoneKey = storageKey + orderDoneSuffix;
    const bookingLockKey = storageKey + bookingLockSuffix;

    const readCachedOrder = () => {
      try {
        const d = sessionStorage.getItem(orderDoneKey);
        if (!d) return null;
        return JSON.parse(d);
      } catch {
        return null;
      }
    };

    const cached = readCachedOrder();
    if (cached) {
      setOrder(cached);
      setWorking(false);
      return undefined;
    }

    if (sessionStorage.getItem(bookingLockKey)) {
      let attempts = 0;
      pollIntervalRef.current = window.setInterval(() => {
        attempts += 1;
        const done = readCachedOrder();
        if (done) {
          if (pollIntervalRef.current) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setOrder(done);
          setWorking(false);
          return;
        }
        if (attempts > 600) {
          if (pollIntervalRef.current) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setError(
            'Could not load your booking confirmation. Refresh this page, or contact support with your Stripe receipt.',
          );
          setWorking(false);
        }
      }, 100);

      return () => {
        if (pollIntervalRef.current) {
          window.clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };
    }

    sessionStorage.setItem(bookingLockKey, sessionId);

    let cancelled = false;

    (async () => {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (!raw) {
          const retryOrder = readCachedOrder();
          if (retryOrder) {
            setOrder(retryOrder);
            return;
          }
          throw new Error('Payment session expired. Please start again from the flight search.');
        }

        const payload = JSON.parse(raw);
        if (payload.type !== 'flight' || !payload.offerId || !Array.isArray(payload.passengers)) {
          throw new Error('Invalid stored flight booking.');
        }

        const { verifyStripeCheckoutSession } = await import('../../lib/stripeFunctions');
        const verified = await verifyStripeCheckoutSession(sessionId, {
          expectedResumeKey: resumeKey,
          expectedPropertyId: payload.offerId,
        });
        if (!verified.ok) {
          throw new Error(verified.error || 'Payment was not completed');
        }

        let res;
        try {
          res = await createFlightOrder({
            offer_id: payload.offerId,
            passengers: payload.passengers,
            type: 'instant',
          });
        } catch (bookErr) {
          const msg = bookErr?.message || '';
          if (isAlreadyBookedDuffelError(msg)) {
            let again = readCachedOrder();
            for (let t = 0; !again && t < 8; t += 1) {
              await new Promise((r) => setTimeout(r, 120));
              again = readCachedOrder();
            }
            if (again) {
              res = { data: again };
            } else {
              throw new Error(
                'This fare was already confirmed. Refresh the page to see your booking, or contact support with your Stripe receipt.',
              );
            }
          } else {
            throw bookErr;
          }
        }

        if (!res?.data) {
          throw new Error(res?.error || 'Duffel could not complete the booking after payment.');
        }

        try {
          sessionStorage.setItem(orderDoneKey, JSON.stringify(res.data));
        } catch {
          /* quota */
        }
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(bookingLockKey);

        setOrder(res.data);
      } catch (e) {
        if (!readCachedOrder()) {
          sessionStorage.removeItem(bookingLockKey);
        }
        if (!cancelled) {
          setError(e?.message || 'Something went wrong after payment.');
        }
      } finally {
        setWorking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resumeKey, sessionId]);

  const primaryReference =
    detail?.duffelReference ||
    order?.booking_reference ||
    order?.booking_references?.[0]?.reference ||
    '';

  const handleDownloadPdf = async () => {
    if (!detail) return;
    setPdfLoading(true);
    try {
      await downloadFlightConfirmationPdf({
        detail,
        primaryReference,
        orderId: order?.id,
      });
    } catch (e) {
      console.error(e);
      alert(e?.message || 'Could not generate PDF. Try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="flight-bconf-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="flight-bconf-main">
        <div className="flight-bconf-container flight-bconf-container--wide">
          {working ? (
            <p className="flight-bconf-working">
              Verifying payment… then creating your booking with the airline via Duffel.
            </p>
          ) : null}

          {!working && error ? (
            <div className="flight-bconf-error">
              <h1 className="flight-bconf-title">Couldn’t complete booking</h1>
              <p>{error}</p>
              <p className="flight-bconf-muted">
                If you were charged, contact support with your Stripe receipt. Do not book the same offer twice without checking.
              </p>
              <Button type="button" variant="primary" onClick={() => navigate('/flights')}>
                Back to flight search
              </Button>
            </div>
          ) : null}

          {!working && !error && order && detail ? (
            <>
              <section className="flight-bconf-success flight-bconf-success--compact">
                <div className="flight-bconf-success__badge" aria-hidden>
                  <span>✓</span>
                </div>
                <h1 className="flight-bconf-title">You&apos;re booked — save these details</h1>
                <p className="flight-bconf-lead">
                  Payment received. Below are your confirmation references, flights, and travelers. Also check your email for the airline confirmation.
                </p>
              </section>

              <section className="flight-bconf-card" aria-labelledby="bconf-refs-heading">
                <h2 id="bconf-refs-heading" className="flight-bconf-card__title">
                  Confirmation &amp; airline references
                </h2>
                <p className="flight-bconf-card__hint">
                  Use these record locators at check-in and with the airline. They may also appear as “PNR” or “booking reference”.
                </p>
                {primaryReference ? (
                  <div className="flight-bconf-primary-ref">
                    <span className="flight-bconf-primary-ref__label">Tourasco / Duffel reference</span>
                    <span className="flight-bconf-primary-ref__value">{primaryReference}</span>
                  </div>
                ) : null}

                {detail.airlineRefs.length > 0 ? (
                  <ul className="flight-bconf-ref-list">
                    {detail.airlineRefs.map((row, idx) => (
                      <li key={`${row.reference}-${idx}`} className="flight-bconf-ref-item">
                        <span className="flight-bconf-ref-item__label">{row.label}</span>
                        <span className="flight-bconf-ref-item__value">{row.reference}</span>
                      </li>
                    ))}
                  </ul>
                ) : primaryReference ? (
                  <p className="flight-bconf-muted flight-bconf-card__foot">
                    Airline-specific references may follow by email when the carrier finishes ticketing.
                  </p>
                ) : null}
              </section>

              <section className="flight-bconf-card" aria-labelledby="bconf-tickets-heading">
                <h2 id="bconf-tickets-heading" className="flight-bconf-card__title">
                  Ticket numbers
                </h2>
                {detail.ticketDocuments.length > 0 ? (
                  <ul className="flight-bconf-ticket-list">
                    {detail.ticketDocuments.map((t, idx) => (
                      <li
                        key={`${t.documentId || t.ticketNumber}-${idx}`}
                        className="flight-bconf-ticket-item"
                      >
                        <span className="flight-bconf-ticket-item__num">
                          {t.displayLabel || t.ticketNumber || `Ticket ${idx + 1}`}
                        </span>
                        {t.documentId ? (
                          <span className="flight-bconf-ticket-item__meta">
                            Document ID: {t.documentId}
                          </span>
                        ) : null}
                        {t.passengerLabel ? (
                          <span className="flight-bconf-ticket-item__meta">
                            Passenger: {t.passengerLabel}
                          </span>
                        ) : null}
                        <span className="flight-bconf-ticket-item__type">{t.type}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="flight-bconf-muted">
                    No e-ticket numbers were returned yet — they often appear in your airline confirmation email once ticketing completes. Keep your references above handy until then.
                  </p>
                )}
              </section>

              <section className="flight-bconf-card" aria-labelledby="bconf-itin-heading">
                <h2 id="bconf-itin-heading" className="flight-bconf-card__title">
                  Your itinerary
                </h2>
                {detail.validatingCarrier ? (
                  <p className="flight-bconf-card__sub">
                    <strong>{detail.validatingCarrier}</strong>
                  </p>
                ) : null}
                {detail.itinerary.length === 0 ? (
                  <p className="flight-bconf-muted">Full schedule details are in your airline confirmation email.</p>
                ) : (
                <ul className="flight-bconf-itin">
                  {detail.itinerary.map((leg) => (
                    <li key={leg.id} className="flight-bconf-itin__leg">
                      <div className="flight-bconf-itin__leg-head">
                        <span className="flight-bconf-itin__slice">{leg.sliceLabel}</span>
                        {leg.carrier ? (
                          <span className="flight-bconf-itin__carrier">{leg.carrier}</span>
                        ) : null}
                        <span className="flight-bconf-itin__flight-no">{leg.flightNum}</span>
                      </div>
                      <div className="flight-bconf-itin__route">
                        <div className="flight-bconf-itin__ap">
                          <span className="flight-bconf-itin__time">{formatTime(leg.depart)}</span>
                          <span className="flight-bconf-itin__code">{leg.originCode}</span>
                          <span className="flight-bconf-itin__name">{leg.originName}</span>
                        </div>
                        <div className="flight-bconf-itin__mid">
                          <span className="flight-bconf-itin__dur">{legDuration(leg.depart, leg.arrive)}</span>
                          <span className="flight-bconf-itin__line" aria-hidden />
                        </div>
                        <div className="flight-bconf-itin__ap flight-bconf-itin__ap--end">
                          <span className="flight-bconf-itin__time">{formatTime(leg.arrive)}</span>
                          <span className="flight-bconf-itin__code">{leg.destCode}</span>
                          <span className="flight-bconf-itin__name">{leg.destName}</span>
                        </div>
                      </div>
                      <p className="flight-bconf-itin__full-dt">
                        Depart {formatDt(leg.depart)} · Arrive {formatDt(leg.arrive)}
                      </p>
                    </li>
                  ))}
                </ul>
                )}
              </section>

              <section className="flight-bconf-card" aria-labelledby="bconf-pax-heading">
                <h2 id="bconf-pax-heading" className="flight-bconf-card__title">
                  Travelers (as ticketed)
                </h2>
                {detail.passengers.length > 0 ? (
                  <ul className="flight-bconf-pax">
                    {detail.passengers.map((p, idx) => (
                      <li key={p.id || `pax-${idx}`} className="flight-bconf-pax__row">
                        <span className="flight-bconf-pax__name">{p.displayName}</span>
                        {p.passengerType ? (
                          <span className="flight-bconf-pax__type">{p.passengerType}</span>
                        ) : null}
                        {p.email ? (
                          <span className="flight-bconf-pax__email">{p.email}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="flight-bconf-muted">Traveler names will match your confirmation email from the airline.</p>
                )}
              </section>

              {detail.totalAmount != null && detail.totalCurrency ? (
                <section className="flight-bconf-card flight-bconf-card--total" aria-labelledby="bconf-pay-heading">
                  <h2 id="bconf-pay-heading" className="flight-bconf-card__title">
                    Amount paid (flight)
                  </h2>
                  <p className="flight-bconf-total-line">
                    <span className="flight-bconf-total-formatted">
                      {formatFrom(detail.totalAmount, detail.totalCurrency)}
                    </span>
                  </p>
                  <p className="flight-bconf-muted flight-bconf-card__foot">
                    Charged via Stripe. Tickets issued through Duffel to the airline.
                  </p>
                </section>
              ) : null}

              <section className="flight-bconf-tips" aria-label="Before you fly">
                <h2 className="flight-bconf-tips__title">Before you fly</h2>
                <ul className="flight-bconf-tips__list">
                  <li>Bring the same ID or passport you used for booking names.</li>
                  <li>Check in online with the airline using your airline reference when available.</li>
                  <li>Arrive at the airport with time for security — especially on international flights.</li>
                  <li>Save this page or screenshot your references in case you need them offline.</li>
                </ul>
              </section>

              <div className="flight-bconf-actions">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={pdfLoading}
                  onClick={handleDownloadPdf}
                  className="flight-bconf-actions__pdf"
                >
                  {pdfLoading ? 'Preparing ticket…' : 'Download ticket'}
                </Button>
                <Button type="button" variant="primary" size="lg" onClick={() => navigate('/flights')}>
                  Search more flights
                </Button>
              </div>
            </>
          ) : null}

          {!working && !error && !order && !resumeKey ? (
            <div className="flight-bconf-error">
              <p>Nothing to show here. Start from the flight search.</p>
              <Button type="button" variant="primary" onClick={() => navigate('/flights')}>
                Flight search
              </Button>
            </div>
          ) : null}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FlightBookingConfirmation;
